/**
 * How many people a person search looks for at one company: the Find person step's people per
 * company, the bulk Find people dialog's stepper, the API's request bounds and the engine's cap
 * (PREC-4398). The dialog quotes a range in these units, so the web and the API read one number.
 */
export const MIN_PEOPLE_PER_COMPANY = 1;
export const MAX_PEOPLE_PER_COMPANY = 3;

/** The positions a search profile can search for (the web's and the API's `IcpPosition`). */
export const FIND_PERSON_POSITIONS = [
	"CEO_AND_SIMILAR",
	"PERSONA_SEGMENT",
	"CUSTOM",
	"DEPARTMENT_SENIORITY",
] as const;

/**
 * Why a search profile cannot be searched with, or why an override rule cannot: each would match
 * anyone at the company (no title, no persona, no department or seniority) or anyone anywhere (a
 * place chosen with none named), and the search would charge for whoever it found.
 */
export type FindPersonProfileProblem =
	| "NO_POSITION"
	| "NO_TITLE"
	| "NO_PERSONA"
	| "NO_DEPARTMENT_OR_SENIORITY"
	| "NO_PLACE";

/** A search profile as the Find person step stores it, as far as its completeness is concerned. */
export interface FindPersonProfileShape {
	position?: string | null;
	title?: string | null;
	personaCategoryId?: number | null;
	department?: string[] | null;
	seniority?: string[] | null;
	countrySelection?: string | null;
	countries?: string[] | null;
	regions?: string[] | null;
}

/** An override rule as the Find person step stores it, as far as whom it searches for is concerned. */
export interface FindPersonOverrideRuleShape {
	action?: string | null;
	actionDetail?: string | null;
	personaCategoryId?: number | null;
	location?: string | null;
	locationDetail?: string | null;
}

const hasText = (value: string | null | undefined): boolean => !!value && value.trim() !== "";

const hasItems = (values: string[] | null | undefined): boolean =>
	Array.isArray(values) && values.some((value) => hasText(value));

/** A place chosen ("only in the following countries or regions") must name one. */
function placeProblem(profile: FindPersonProfileShape): FindPersonProfileProblem | null {
	return profile.countrySelection === "CUSTOM" &&
		!hasItems(profile.countries) &&
		!hasItems(profile.regions)
		? "NO_PLACE"
		: null;
}

/**
 * What a search profile still needs before it can be searched with, or null when it is complete
 * (PREC-4398). The rule the Find person step's editor enforces while a profile is open
 * (`useIcpValidation`), stated over the stored profile itself, so the bulk dialog (which shows
 * profiles it did not open) and the API (which searches them) decide alike. A profile saved before
 * positions existed names only its title, and the search searches that title.
 */
export function findPersonProfileProblem(
	profile: FindPersonProfileShape
): FindPersonProfileProblem | null {
	const { position } = profile;

	if (!position) {
		return hasText(profile.title) ? placeProblem(profile) : "NO_POSITION";
	}

	if (!(FIND_PERSON_POSITIONS as readonly string[]).includes(position)) {
		return "NO_POSITION";
	}

	if (position === "CUSTOM" && !hasText(profile.title)) {
		return "NO_TITLE";
	}

	if (position === "PERSONA_SEGMENT" && !profile.personaCategoryId) {
		return "NO_PERSONA";
	}

	if (
		position === "DEPARTMENT_SENIORITY" &&
		!hasItems(profile.department) &&
		!hasItems(profile.seniority)
	) {
		return "NO_DEPARTMENT_OR_SENIORITY";
	}

	return placeProblem(profile);
}

/**
 * What an override rule still needs before the profile it stands for can be searched with, or
 * null: a custom title rule names a title, a persona rule a persona, a specific-place rule a place.
 * A rule whose condition is incomplete simply never matches, so only its action is checked.
 */
export function findPersonOverrideRuleProblem(
	rule: FindPersonOverrideRuleShape
): FindPersonProfileProblem | null {
	if (rule.action === "custom" && !hasText(rule.actionDetail)) {
		return "NO_TITLE";
	}

	if (rule.action === "persona" && !rule.personaCategoryId) {
		return "NO_PERSONA";
	}

	if (!["ceo", "persona", "custom"].includes(rule.action ?? "")) {
		return "NO_POSITION";
	}

	return rule.location === "specific" && !hasText(rule.locationDetail) ? "NO_PLACE" : null;
}
